import { formatDistanceToNow } from 'date-fns';
import { Box, Typography, Avatar, Tooltip, IconButton, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Delete, InsertDriveFile, AutoAwesome, SmartToy } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


const ChatMessage = ({
  message,
  isOwn,
  onDelete,
  canDelete
}) => {
  const theme = useTheme();
  const sender = message.sender;
  const isAI = message.metadata?.isAI || sender?.role === "ai-bot";
  const senderName = isAI
    ? 'DigitAir AI'
    : sender
      ? `${sender.firstName} ${sender.lastName}`
      : 'Unknown';

  const initials = senderName.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const timeAgo = message.createdAt
    ? formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })
    : '';

  const aiBg = theme.palette.mode === 'dark'
    ? 'rgba(99, 102, 241, 0.15)'
    : 'rgba(99, 102, 241, 0.08)';

  const bubbleBg = isOwn
    ? theme.palette.slate.main
    : isAI
      ? aiBg
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[200];

  const textColor = isOwn ? '#fff' : theme.palette.text.primary;

  return (
    <Box
      display="flex"
      flexDirection={isOwn ? 'row-reverse' : 'row'}
      alignItems="flex-start"
      gap={1}
      sx={{ mb: 1, px: 1 }}
    >
      {/* Avatar */}
      {!isOwn && (
        <Tooltip title={senderName}>
          {isAI ? (
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', flexShrink: 0 }}>
              <SmartToy sx={{ fontSize: 14 }} />
            </Avatar>
          ) : (
            <Avatar
              src={sender?.picturePath || undefined}
              sx={{ width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0 }}
            >
              {initials}
            </Avatar>
          )}
        </Tooltip>
      )}

      {/* Bubble */}
      <Box
        sx={{
          maxWidth: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwn ? 'flex-end' : 'flex-start',
          gap: 0.25,
        }}
      >
        {!isOwn && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ pl: 0.5 }}
          >
            {senderName}
          </Typography>
        )}

        <Box
          sx={{
            position: 'relative',
            px: 1.5,
            py: 1,
            borderRadius: isOwn ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
            backgroundColor: bubbleBg,
            color: textColor,
            wordBreak: 'break-word',
            '&:hover .msg-actions': { opacity: 1 },
          }}
        >
          {/* Text content */}
          {message.content && (
            isAI ? (
              <Box sx={{
                '& p': { m: 0, fontSize: '0.85rem', color: textColor },
                '& p + p': { mt: 0.5 },
                '& table': { borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' },
                '& th, & td': { border: '1px solid', borderColor: 'divider', px: 0.75, py: 0.4 },
                '& th': { bgcolor: 'action.hover', fontWeight: 600 },
                '& code': { bgcolor: 'action.hover', borderRadius: '3px', px: 0.5, fontFamily: 'monospace', fontSize: '0.8em' },
                '& ul, & ol': { m: 0, pl: 2 },
                '& li': { mb: 0.25, fontSize: '0.85rem', color: textColor },
                '& strong': { fontWeight: 600, color: textColor },
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', color: textColor }}
              >
                {message.content}
              </Typography>
            )
          )}

          {/* Attachments */}
          {message.attachments?.map((att, i) =>
            att.mimeType?.startsWith('image/') ? (
              <Box
                key={i}
                component="img"
                src={att.url}
                alt={att.name}
                sx={{
                  maxWidth: 220,
                  maxHeight: 180,
                  borderRadius: 1,
                  mt: message.content ? 0.5 : 0,
                  display: 'block',
                  cursor: 'pointer',
                }}
                onClick={() => window.open(att.url, '_blank')}
              />
            ) : (
              <Box
                key={i}
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  mt: message.content ? 0.5 : 0,
                  cursor: 'pointer',
                  opacity: 0.9,
                  '&:hover': { opacity: 1 },
                }}
                onClick={() => window.open(att.url, '_blank')}
              >
                <InsertDriveFile 
                  fontSize="small" 
                  sx={{ color: textColor }} 
                />
                <Typography 
                  variant="caption" 
                  sx={{ color: textColor, textDecoration: 'underline' }}
                >
                  {att.name}
                </Typography>
              </Box>
            )
          )}

          {/* Delete action */}
          {canDelete && (
            <Box
              className="msg-actions"
              sx={{
                position: 'absolute',
                top: 8,
                [isOwn ? 'left' : 'right']: -24,
                opacity: 0,
                transition: 'opacity 0.15s',
              }}
            >
              <IconButton 
                size="small" 
                onClick={() => onDelete?.(message._id)} 
                sx={{ p: 0.25 }}
              >
                <Delete sx={{ fontSize: 14, color: 'text.secondary' }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {isAI && message.metadata?.toolsUsed?.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
            {message.metadata.toolsUsed.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                icon={<AutoAwesome sx={{ fontSize: '10px !important' }} />}
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            ))}
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ px: 0.5 }}
        >
          {timeAgo}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatMessage;
